import { NavigationMenu } from "radix-ui";
import * as React from "react";
import { FC, useLayoutEffect, useRef, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import useAuthRoute from "~/hook/useAuthRoute";
import { useFontStyle } from "~/hook/useFontStyle";
import { useImagePreloader } from "~/hook/useImagePreloader";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";
import { getLength, wantsDesktop } from "~/utils/helper-funcations";
import HeaderImage from "../HeaderImage";
import { MenuDataType, MenuGroup, PropsType } from "../mobile-menu/type";
import SearchBar from "../SearchBar";
import BannerItem from "./BannerItem";
import IconItem from "./IconItem";
import ListItem from "./ListItem";
import "./style.css";
import TextItem from "./TextItem";

type DesktopPropsType = {
  categories: MenuDataType[];
  isSticky?: boolean;
  isSupperHeader?: boolean;
  HideNavbarForYoutubeVideoPlayer?: boolean;
};

const GRID_WIDTH = {
  wide: "wide",
  narrow: "narrow",
};

const cellOrder: Record<string, number> = {
  a1: 1,
  a2: 2,
  a3: 3,
  b1: 1,
  b2: 2,
  b3: 3,
  c1: 1,
  c2: 2,
  c3: 3,
};

const COMPONENT_MAPPING: Record<string, FC<PropsType>> = {
  icon: IconItem,
  banner: BannerItem,
  list: ListItem,
  text: TextItem,
};

const DesktopMenu: React.FC<DesktopPropsType> = ({ categories, isSticky, isSupperHeader = false, HideNavbarForYoutubeVideoPlayer = false }) => {
  const [selectedIndex, setSelectedLength] = useState<number>(0);
  const { navigate } = useLocale();
  const { medium } = useFontStyle();
  const { handleNavigate } = useAuthRoute();
  const menuItemRefs = useRef<(HTMLElement | null)[]>([]);

  // Initialize image preloader
  const { isImagePreloaded } = useImagePreloader(categories);

  const [viewportPosition, setViewportPosition] = useState<{ left: number | string; transform: string }>({ left: 0, transform: "translateX(0)" });

  useLayoutEffect(() => {
    const currentCategory = categories[selectedIndex];
    const shouldCenter =
      selectedIndex === 0 ||
      selectedIndex === 1 ||
      ((selectedIndex === categories.length - 1 || selectedIndex === categories.length - 2) && currentCategory?.menu_group?.length > 2);

    if ((selectedIndex === 0 || selectedIndex === 1) && currentCategory?.menu_group?.length <= 2) {
      setViewportPosition({
        left: 0,
        transform: "translateX(0)",
      });
      return;
    }

    if (shouldCenter || currentCategory?.menu_group?.length > 2) {
      setViewportPosition({
        left: "50%",
        transform: "translateX(-50%)",
      });
    } else {
      const currentMenuItem = menuItemRefs.current[selectedIndex];
      if (currentMenuItem) {
        const rect = currentMenuItem.getBoundingClientRect();
        const parentRect = currentMenuItem.closest(".nav-list-gap")?.getBoundingClientRect();

        if (parentRect) {
          const itemCenterX = rect.left + rect.width / 2 - parentRect.left;
          setViewportPosition({
            left: itemCenterX,
            transform: "translateX(-50%)",
          });
        }
      }
    }
  }, [selectedIndex, categories]);

  const sortedData = (data: MenuGroup[]) => {
    return data.sort((a, b) => {
      return cellOrder[a.cell_id] - cellOrder[b.cell_id];
    });
  };

  // function to organize items by priority and column
  const organizeMenuItems = (data: MenuGroup[]) => {
    const highPriority = data?.filter((item) => item.width === GRID_WIDTH.narrow) || [];
    const lowPriority = data?.filter((item) => item.width === GRID_WIDTH.wide) || [];

    const organizeByColumn = (items: MenuGroup[]) => {
      const columns = { col1: [] as MenuGroup[], col2: [] as MenuGroup[], col3: [] as MenuGroup[] };

      items.forEach((item) => {
        if (["a1", "a2", "a3"].includes(item.cell_id)) {
          columns.col1.push(item);
        } else if (["b1", "b2", "b3"].includes(item.cell_id)) {
          columns.col2.push(item);
        } else if (["c1", "c2", "c3"].includes(item.cell_id)) {
          columns.col3.push(item);
        }
      });

      return columns;
    };

    return {
      highPriority: organizeByColumn(highPriority),
      lowPriority: organizeByColumn(lowPriority),
    };
  };

  // Reusable column renderer
  const renderColumn = (columnData: MenuGroup[], items: MenuDataType, isLastColumn: boolean = false) => {
    if (columnData.length === 0) return null;

    return (
      <div className={cn("columns-1", !isLastColumn && getLength(items?.menu_group) > 1 ? "right-divider" : "")}>
        {sortedData(columnData).map((group) => {
          const Component = COMPONENT_MAPPING[group.disp_type];
          const isVisible = wantsDesktop(group.show_menu);
          return isVisible ? (
            <div key={group.id} className={cn("break-inside-avoid", items.menu_group.length > 1 ? "mb-5" : "mb-0")}>
              <Component
                shape={group?.shape}
                data={group.menu_item}
                category_name={group.show_name === 1 ? group.name : null}
                displayItem={group.width}
                isImagePreloaded={isImagePreloaded}
              />
            </div>
          ) : null;
        })}
      </div>
    );
  };

  return (
    <NavigationMenu.Root
      className={cn("w-[calc(100vw-200px)] relative flex items-center md:w-full ", isSticky ? "justify-between" : "justify-center")}
    >
      <div className={cn(isSticky ? "block" : "hidden")}>
        <button className="flex justify-center items-center cursor-pointer" onClick={() => navigate("/")}>
          <div className="w-[45px] h-[45px] lg:w-[50px] lg:h-[50px] aspect-square rounded-full">
            <img
              src="/images/maharaj-header-icon.webp"
              alt="swaminarayan bhagawan"
              className="w-full h-full rounded-full block 2xl:hidden nav-img-second select-none"
            />
          </div>
          <div className="w-full h-full hidden 2xl:block nav-img-first">
            {/* <img src="/icons/logo.svg" alt="swaminarayan bhagawan"  /> */}
            <HeaderImage />
          </div>
        </button>
      </div>

      <NavigationMenu.List
        className={cn(
          "flex flex-wrap justify-center items-center p-[2px]  nav-list-gap rounded-[6px] list-none relative",
          isSticky ? "gap-x-5 2xl:gap-x-10" : "md:gap-x-7 2xl:gap-x-10",
        )}
      >
        {categories?.map((items, index) => {
          const { highPriority, lowPriority } = organizeMenuItems(items?.menu_group || []);
          const isVisible = wantsDesktop(items.show_menu);

          return (
            isVisible && (
              <React.Fragment key={items.id}>
                <NavigationMenu.Item ref={(el) => (menuItemRefs.current[index] = el)} onMouseEnter={() => setSelectedLength(index)} key={items.id}>
                  {items?.menu_group?.length > 0 ? (
                    <>
                      <NavigationMenu.Trigger
                        onClick={() => {
                          if (items.type === "navigation") handleNavigate(items.navigate_to);
                          return;
                        }}
                        className={cn("menu-trigger body_text_l", medium)}
                      >
                        {items.name} <FaCaretDown className="CaretDown -mt-1 " aria-hidden />
                      </NavigationMenu.Trigger>
                    </>
                  ) : (
                    <button className={cn("menu-trigger md:text-16 2xl:text-lg", medium)} onClick={() => handleNavigate(items.navigate_to)}>
                      <span>{items.name}</span>
                    </button>
                  )}

                  <NavigationMenu.Content className="NavigationMenuContent !overscroll-contain">
                    <div className="flex p-3 gap-x-4">
                      {/* Render columns with priority logic */}
                      {[1, 2, 3].map((colNum) => {
                        const colKey = `col${colNum}` as keyof typeof highPriority;
                        const highPriorityCol = highPriority[colKey];
                        const lowPriorityCol = lowPriority[colKey];

                        // Render high priority first, fallback to low priority
                        const columnToRender = highPriorityCol.length > 0 ? highPriorityCol : lowPriorityCol;
                        if (columnToRender.length === 0) return null;

                        // Only show a right divider when there is at least one
                        // subsequent column that also has content to render.
                        const hasNextColumn = [colNum + 1, colNum + 2]
                          .filter((n) => n <= 3)
                          .some((n) => {
                            const key = `col${n}` as keyof typeof highPriority;
                            return highPriority[key].length > 0 || lowPriority[key].length > 0;
                          });

                        return renderColumn(columnToRender, items, !hasNextColumn);
                      })}
                    </div>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              </React.Fragment>
            )
          );
        })}

        <NavigationMenu.Indicator className={cn("NavigationMenuIndicator", isSticky ? "flex" : "hidden")}>
          <div className="Arrow" />
        </NavigationMenu.Indicator>

        <div className="absolute top-full z-50" style={{ left: viewportPosition.left, transform: viewportPosition.transform }}>
          {categories[selectedIndex]?.menu_group?.length > 0 ? (
            <NavigationMenu.Viewport className={cn("NavigationMenuViewport scrollbar-none ", isSticky ? "left-20 2xl:left-0 !-mt-[0.1px]" : "")} />
          ) : null}
        </div>
      </NavigationMenu.List>

      <div className={cn("justify-end items-center", isSticky ? "flex" : "hidden")}>
        <SearchBar
          width={"md:w-[150px] lg:!w-[250px] 2xl:w-[250px]"}
          isSticky={isSticky}
          isSupperHeader={isSupperHeader}
          isYoutubeVideoLayout={HideNavbarForYoutubeVideoPlayer}
        />
      </div>
    </NavigationMenu.Root>
  );
};

export default DesktopMenu;
